import React, { createContext, useState, useContext, useEffect } from 'react';
import { login, getUserProfile } from '../services/apiService';
import { useGoogleLogin } from '@react-oauth/google';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Helper functions to safely access localStorage
const safeGetItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error('Error accessing localStorage:', error);
    return null;
  }
};

const safeSetItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.error('Error setting localStorage:', error);
    return false;
  }
};

const safeRemoveItem = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Error removing from localStorage:', error);
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(safeGetItem('token'));

  // Check for existing token on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (authToken) {
        try {
          const userData = await getUserProfile();
          setUser(userData);
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setAuthToken(null);
          safeRemoveItem('token');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [authToken]);

  const googleLogin = useGoogleLogin({
    flow: 'implicit', // Use implicit flow to avoid popup issues
    onSuccess: async (response) => {
      try {
        setLoading(true);
        
        try {
          console.log('Google login successful, token:', response.access_token);
          const result = await login(response.access_token);
          
          if (result && result.token) {
            setAuthToken(result.token);
            safeSetItem('token', result.token);
            setUser(result.user);
          } else {
            throw new Error('No token received from server');
          }
        } catch (error) {
          console.error('Login error:', error);
          alert('Login failed: ' + error.message);
        }
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google login error:', error);
      alert('Google login failed. Please try again.');
      setLoading(false);
    }
  });

  const logout = () => {
    safeRemoveItem('token');
    setAuthToken(null);
    setUser(null);
  };

  const value = {
    user,
    loading,
    googleLogin,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 